import React, { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { FormField } from "components/form/FormField/FormField";

import style from "../AddVaccineForm.module.css";

export const SearchableSelectField = ({
  name,
  label,
  options,
  placeholder = "Search...",
  disabled = false,
  renderOption,
  required = false,
  ariaRequired = false,
}) => {
  const { control, setValue, watch } = useFormContext();

  const enabledOptions = options.filter((opt) => opt.enabled !== false);
  // A field with one possible answer is not a choice, so it should not look like one.
  // A search box offering a single option invites the GP to open it, read one line and
  // close it again — and worse, reads as though something else might be selectable
  // (KIN-1679 narrowed RSV's route to intramuscular, which is where this shows).
  const onlyOption = enabledOptions.length === 1 ? enabledOptions[0] : null;
  const currentValue = watch(name);

  // The display-only input registers nothing, so the value has to be put in form state
  // here. Callers that already set it — RSV's route effect, for instance — write the
  // same value, so this is a guarantee rather than a second source of truth.
  useEffect(() => {
    if (onlyOption && currentValue !== onlyOption.value) {
      setValue(name, onlyOption.value, { shouldValidate: true });
    }
  }, [onlyOption, currentValue, name, setValue]);

  if (onlyOption) {
    return (
      <FormField name={name}>
        {/* No asterisk: nothing is being asked of the user, and marking a field they
            cannot change as required is a demand they cannot act on. */}
        <label className={style.pregnancyFormLabel}>{label}</label>
        <input
          type="text"
          className="form-control"
          value={onlyOption.label}
          disabled
        />
      </FormField>
    );
  }

  return (
    <FormField name={name}>
      <label className={style.pregnancyFormLabel}>
        {label}
        {required ? " *" : ""}
      </label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Autocomplete
            disabled={disabled}
            options={enabledOptions}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                const found = enabledOptions.find((o) => o.value === option);
                return found ? found.label : option;
              }
              return option.label || "";
            }}
            value={
              enabledOptions.find((opt) => opt.value === field.value) || null
            }
            onChange={(_, newValue) => {
              field.onChange(newValue ? newValue.value : "");
            }}
            isOptionEqualToValue={(option, value) => {
              if (typeof value === "string") {
                return option.value === value;
              }
              return option.value === value?.value;
            }}
            {...(renderOption && { renderOption })}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                inputProps={{
                  ...params.inputProps,
                  "aria-required":
                    required || ariaRequired ? "true" : undefined,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "48px",
                    borderRadius: "12px !important",
                    backgroundColor: disabled ? "#eeeef0" : "#fdfdfd",
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: "16px",
                    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                    border: disabled ? "1px solid #b7b8c2" : "1px solid #8f8fa1",
                    "& fieldset": {
                      border: "none !important",
                    },
                    "&:hover": {
                      border: disabled ? "1px solid #b7b8c2" : "1px solid #8f8fa1",
                    },
                    "&.Mui-focused": {
                      border: "1px solid #006546",
                    },
                    "& .MuiAutocomplete-input": {
                      padding: "0 !important",
                      paddingLeft: "8px !important",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: "16px",
                    color: disabled ? "#717186" : "#26252c",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#8f8fa1",
                    opacity: 1,
                  },
                }}
              />
            )}
            sx={{
              "& .MuiAutocomplete-popupIndicator": {
                color: "#26252c",
              },
              "& .MuiAutocomplete-clearIndicator": {
                color: "#717186",
              },
            }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: "12px",
                  marginTop: "4px",
                  boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.15)",
                  "& .MuiAutocomplete-listbox": {
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: "16px",
                    "& .MuiAutocomplete-option": {
                      padding: "12px 16px",
                      "&:hover": {
                        backgroundColor: "#f7f7f8",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#f7f7f8",
                      },
                      '&[aria-selected="true"]': {
                        backgroundColor: "#e7efeb",
                      },
                    },
                  },
                },
              },
            }}
          />
        )}
      />
    </FormField>
  );
};
